/**
 * CYP ASD History — Developmental History Review Page
 *
 * First step of the Diagnostic Developmental History flow, preceding the
 * Semi-Structured Interview (SSI). Presents pre-filled referral and
 * background information across seven sections that mirror the triage
 * proforma workflow from the Referral Management System.
 *
 * The clinician reviews this information before proceeding to the SSI.
 * All data is hardcoded dummy data for the mockup phase — in production
 * this would be pulled from the patient's referral and triage forms.
 *
 * Layout mirrors the RMS triage proforma:
 *   - Left sidebar with numbered section navigation
 *   - Right content area with line-item labels on the left, answer text
 *     areas on the right (single or dual-column for school + parent/carer)
 *   - Optional StructuredNotesEditor at the bottom of each section
 *   - Fixed footer with Back / Next / Proceed navigation
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  AcademicCapIcon,
  HomeIcon,
} from '@heroicons/react/outline';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import StructuredNotesEditor from '../12_observation/components/StructuredNotesEditor';
import HISTORY_SECTIONS from './config/cypAsdHistoryConfig';

// ─────────────────────────────────────────────────────────────────────────────
//  Sidebar navigation — numbered circles, mirrors ProformaSideNav from RMS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Section navigation with numbered step circles.
 * Active section gets a brand-primary circle, visited sections show
 * a green circle with the number, and unvisited ones show a grey number.
 * "Visited" is purely based on navigation — notes are optional and
 * don't affect the visited indicator.
 */
function HistorySideNav({ sections, currentIndex, onSelect, visitedSections }) {
  return (
    <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex-shrink-0">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          History Sections
        </span>
      </div>

      {/* Navigation items */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
        {sections.map((section, idx) => {
          const isActive = idx === currentIndex;
          const isVisited = visitedSections.has(idx);

          return (
            <button
              key={section.id}
              onClick={() => onSelect(idx)}
              className={`
                w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-2.5
                transition-all duration-200 ease-in-out
                ${isActive
                  ? 'bg-gray-100 border border-gray-200 text-primary font-medium'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800 border border-transparent'}
              `}
            >
              {/* Numbered circle — green once visited (even when active), brand-primary only for
                  un-visited active, grey for un-visited inactive */}
              <div className={`
                w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                text-xs font-bold transition-all duration-200
                ${isVisited
                  ? 'bg-emerald-500 text-white'
                  : isActive
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-500'}
              `}>
                {idx + 1}
              </div>

              <span className="truncate leading-tight">{section.shortTitle}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  DisplayTextArea — read-only text box matching the RMS proforma style
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Read-only text display area with lightweight rich-text rendering.
 *
 * Supported formatting in the value string:
 *   • Lines starting with "• " render as indented bullet points
 *   • Empty lines produce a small vertical spacer
 *   • All other lines render as plain text
 *   • Text wrapped in **bold** renders as <strong>
 */
function DisplayTextArea({ value, placeholder = 'No information provided' }) {
  const isEmpty = !value;

  const renderContent = (text) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      if (line.startsWith('• ')) {
        return (
          <div key={idx} className="flex items-start gap-1.5">
            <span className="text-gray-400 flex-shrink-0 leading-relaxed">•</span>
            <span className="leading-relaxed">{renderBold(line.substring(2))}</span>
          </div>
        );
      }
      if (line === '') {
        return <div key={idx} className="h-1.5" />;
      }
      return <div key={idx} className="leading-relaxed">{renderBold(line)}</div>;
    });
  };

  const renderBold = (text) => {
    if (!text.includes('**')) return text;
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1
        ? <strong key={i} className="font-semibold">{part}</strong>
        : part
    );
  };

  return (
    <div className={`
      flex-1 min-w-0 min-h-[40px] px-3 py-2 text-sm border rounded-md
      ${isEmpty
        ? 'text-gray-400 italic border-gray-200 bg-white'
        : 'text-gray-900 border-gray-200 bg-white'}
    `}>
      {isEmpty ? placeholder : renderContent(value)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Line item rendering — single-column or dual-column per question
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders a single line item row: label on the left, answer(s) on the right.
 * For dual-respondent sections, two answer columns are shown side by side
 * (school + parent/carer). For single-respondent sections, one answer column
 * spans the full width.
 */
function LineItemRow({ question, isDual }) {
  if (isDual) {
    return (
      <div className="flex items-start mb-4">
        {/* Line item label */}
        <div className="w-56 pr-4 flex-shrink-0">
          <span className="font-semibold text-sm text-gray-800">{question.label}</span>
        </div>

        {/* Two-column answers — columns stretch to match the taller side */}
        <div className="flex-1 flex gap-4 items-stretch">
          <DisplayTextArea value={question.schoolAnswer} placeholder="N/A" />
          <DisplayTextArea value={question.parentAnswer} placeholder="N/A" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start mb-4">
      {/* Line item label */}
      <div className="w-56 pr-4 flex-shrink-0">
        <span className="font-semibold text-sm text-gray-800">{question.label}</span>
      </div>

      {/* Single answer column */}
      <div className="flex-1">
        <DisplayTextArea value={question.answer} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Section content panel — header with optional column labels + line items
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders the content for the active section. Dual-respondent sections
 * display column headers (School Representative / Parent/Carer) above the
 * answer columns, matching the RMS DualRespondentDomainPanel layout.
 *
 * Includes an optional StructuredNotesEditor at the bottom so clinicians
 * can jot down free-form notes per section. Notes are completely optional
 * and do not affect visited-status indicators.
 */
function SectionPanel({ section, notes, onNotesChange }) {
  const isDual = section.isDual === true;

  return (
    <div className="space-y-6">
      {/* Referral / history data panel */}
      <div className="bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm">
        {/* Panel header — title + respondent column labels for dual sections */}
        <div className="flex items-center mb-2">
          <h4 className="font-bold text-lg w-56 flex-shrink-0">{section.title}</h4>

          {isDual && (
            <div className="flex-1 flex gap-4">
              <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-primary">
                <AcademicCapIcon className="w-4 h-4" />
                <span>School Representative</span>
              </div>
              <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-primary">
                <HomeIcon className="w-4 h-4" />
                <span>Parent / Carer</span>
              </div>
            </div>
          )}
        </div>

        {/* Line items */}
        <div className="border-t border-gray-200 pt-4">
          {section.questions.map((q) => (
            <LineItemRow key={q.id} question={q} isDual={isDual} />
          ))}
        </div>
      </div>

      {/* Clinician notes — optional free-form notes per section.
          Uses the same table-style layout (brand-primary heading + grey body)
          as the Observation module for visual consistency. */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden text-left">
        <div className="bg-primary px-4 py-3">
          <h3 className="text-sm font-bold text-white">Notes</h3>          
        </div>
        <div className="bg-gray-50 px-4 py-4">
          <StructuredNotesEditor
            notes={notes}
            onChange={onNotesChange}
            placeholder="Type any notes about this section…"
            rows={4}
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function CypAsdHistory() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  // ── localStorage keys — scoped per patient so multiple patients
  //    don't share History state during testing. ──────────────────────
  const notesKey   = `cyp-asd-history-notes-${patientId}`;
  const navKey     = `cyp-asd-history-nav-${patientId}`;

  // ── Navigation state — persisted so clinicians don't lose their
  //    place when navigating to SSI/Observation and back. ────────────
  const [currentIndex, setCurrentIndex] = useState(() => {
    try {
      const cached = localStorage.getItem(navKey);
      if (cached) return JSON.parse(cached).currentIndex ?? 0;
    } catch { /* ignore */ }
    return 0;
  });

  const [visitedSections, setVisitedSections] = useState(() => {
    try {
      const cached = localStorage.getItem(navKey);
      if (cached) {
        const arr = JSON.parse(cached).visited;
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch { /* ignore */ }
    return new Set([0]);
  });

  // Persist navigation state whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(navKey, JSON.stringify({
        currentIndex,
        visited: Array.from(visitedSections),
      }));
    } catch { /* ignore */ }
  }, [navKey, currentIndex, visitedSections]);

  /**
   * Per-section clinician notes, keyed by section id.
   * Each entry is an array of note objects consumed by StructuredNotesEditor.
   * Persisted to localStorage so notes survive page refreshes and
   * navigation to other modules (SSI, Observation, MDT Review).
   */
  const [sectionNotes, setSectionNotes] = useState(() => {
    try {
      const cached = localStorage.getItem(notesKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return {};
  });

  useEffect(() => {
    try {
      localStorage.setItem(notesKey, JSON.stringify(sectionNotes));
    } catch { /* quota exceeded — ignore in mockup */ }
  }, [notesKey, sectionNotes]);

  const currentSection = HISTORY_SECTIONS[currentIndex];
  const isFirstSection = currentIndex === 0;
  const isLastSection = currentIndex === HISTORY_SECTIONS.length - 1;

  /**
   * Navigate to a section by index, marking it as visited and
   * scrolling the content area back to the top.
   */
  const goToSection = useCallback((idx) => {
    setCurrentIndex(idx);
    setVisitedSections(prev => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) goToSection(currentIndex - 1);
  }, [currentIndex, goToSection]);

  const handleNext = useCallback(() => {
    if (currentIndex < HISTORY_SECTIONS.length - 1) {
      goToSection(currentIndex + 1);
    }
  }, [currentIndex, goToSection]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0 });
    }
  }, [currentIndex]);

  const handleProceedToCriteria = useCallback(() => {
    navigate(`/patient/${patientId}/proforma/cyp-asd/semi-structured-interview`);
  }, [navigate, patientId]);

  /** Update notes for the currently active section */
  const handleNotesChange = useCallback((newNotes) => {
    setSectionNotes(prev => ({
      ...prev,
      [currentSection.id]: newNotes,
    }));
  }, [currentSection]);

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['CYP ASD Assessment'],
    ['Diagnostic Developmental History'],
    ['History'],
    ...(currentSection ? [[currentSection.shortTitle]] : []),
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Main layout — sidebar spans full height (including footer row),
          content area + footer share the remaining space on the right. */}
      <div className="flex h-[calc(100vh-8rem)]">
        {/* Left sidebar — full height, white background, separated by a vertical border */}
        <HistorySideNav
          sections={HISTORY_SECTIONS}
          currentIndex={currentIndex}
          onSelect={goToSection}
          visitedSections={visitedSections}
        />

        {/* Right column — scrollable content area + fixed footer stacked vertically */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Scrollable content */}
          <div ref={contentRef} className="flex-1 overflow-y-auto p-8">
            <SectionPanel
              section={currentSection}
              notes={sectionNotes[currentSection.id] || []}
              onNotesChange={handleNotesChange}
            />
          </div>

          {/* Fixed footer — its left edge aligns with the sidebar's right edge */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-8 py-3 flex items-center justify-between">
            <div className="w-40">
              {!isFirstSection && (
                <button
                  onClick={handlePrev}
                  className="flex items-center py-2 px-4 rounded-md border
                    transition-all duration-200 text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  <ChevronLeftIcon className="w-5 h-5 mr-2" />
                  <span className="font-semibold text-sm">Back</span>
                </button>
              )}
            </div>

            {/* Section counter — e.g. "3 of 7" */}
            <span className="text-sm text-gray-400 font-medium">
              {currentIndex + 1} of {HISTORY_SECTIONS.length}
            </span>

            <div className="flex justify-end">
              {!isLastSection ? (
                <button
                  onClick={handleNext}
                  className="flex items-center py-2 px-4 rounded-md shadow-sm
                    transition-all duration-200 text-white bg-primary hover:bg-primary/90"
                >
                  <span className="font-semibold text-sm">Next</span>
                  <ChevronRightIcon className="w-5 h-5 ml-2" />
                </button>
              ) : (
                <button
                  onClick={handleProceedToCriteria}
                  className="flex items-center py-2 px-5 rounded-md shadow-sm whitespace-nowrap
                    transition-all duration-200 text-white bg-emerald-600 hover:bg-emerald-700"
                >
                  <span className="font-semibold text-sm">Proceed to Criteria</span>
                  <ChevronRightIcon className="w-5 h-5 ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
